import { customType, index, jsonb, pgEnum, pgTable, text, timestamp } from "drizzle-orm/pg-core";

/** What a bot job asks a worker to do. The values are wire strings the bots read. */
export enum BotJobType {
  /** Buy an order's cards on Fanatics Collect (fanatics-bot). */
  FANATICS_BUY = "FANATICS_BUY",
  /** Render one StockX page in a real browser and send back what it loaded (stockx-bot). */
  STOCKX_PAGE = "STOCKX_PAGE",
}

/** Lifecycle of one bot job. CLAIMED carries a lease that returns the job to PENDING. */
export enum BotJobStatus {
  PENDING = "PENDING",
  CLAIMED = "CLAIMED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export const botJobTypeEnum = pgEnum("bot_job_type", [
  BotJobType.FANATICS_BUY,
  BotJobType.STOCKX_PAGE,
]);

export const botJobStatusEnum = pgEnum("bot_job_status", [
  BotJobStatus.PENDING,
  BotJobStatus.CLAIMED,
  BotJobStatus.COMPLETED,
  BotJobStatus.FAILED,
]);

/**
 * A `bytea` column read as a Buffer. postgres.js returns a Buffer already; PGlite returns a
 * Uint8Array, which `Buffer.from` copies into one.
 */
const bytea = customType<{ data: Buffer; driverData: Uint8Array }>({
  dataType: () => "bytea",
  fromDriver: (value) => Buffer.from(value),
});

/**
 * Bot worker jobs (Fanatics buy cards, StockX page renders). Ported from boxed Dynamo Job table —
 * claim/complete/fail with a releasesAt lease for stale CLAIMED recovery.
 */
export const botJobs = pgTable(
  "bot_jobs",
  {
    id: text("id").primaryKey(),
    type: botJobTypeEnum("type").notNull(),
    status: botJobStatusEnum("status").notNull(),
    payload: jsonb("payload").notNull().$type<Record<string, unknown>>(),
    workerId: text("worker_id"),
    releasesAt: timestamp("releases_at", { withTimezone: true }),
    result: jsonb("result").$type<Record<string, unknown>>(),
    failureReason: text("failure_reason"),
    failureData: jsonb("failure_data").$type<Record<string, unknown>>(),
    /**
     * A STOCKX_PAGE product job's complete body, brotli-compressed, kept only until the product
     * fetch worker has read it; NULL on every other job and after that read.
     */
    capture: bytea("capture"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [index("bot_jobs_type_status_created_idx").on(t.type, t.status, t.createdAt)],
);
